import { Injectable, computed } from '@angular/core';
import { RevalidatedHttpService, RevalidatedResource } from 'src/app/core/http-cache/revalidated-http.service';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { Ga4AccountVM } from 'src/app/models/ga4-account-vm';
import { catchError } from 'rxjs/operators';
import { Apps, StoreAppsResponse, StoreAppCreationResponse, StoreIntegrationResponse, StoreEditTokenResponse } from 'src/app/models/apps';
import { throwError } from 'rxjs';
import { SanctumService } from 'src/app/services/sanctum.service';
import { AuthHeaderService } from 'src/app/services/auth-header.service';
import { switchMap } from 'rxjs/operators';
import { from } from 'rxjs';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { GoogleAccountVM } from 'src/app/models/google-account-vm';
import { NuvemshopAccountVM } from 'src/app/models/nuvemshop-account-vm';

export interface RemoveTiktokResp {
  ok: boolean;
  removed_open_id: string;
  remaining: number;
}
export interface TikTokOauthResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  open_id: string;
  union_id?: string;

  display_name?: string;
  username?: string;
  avatar_url?: string;
  avatar_url_100?: string;
  avatar_large_url?: string;

  scope?: string[] | string;
  token_type?: string;
}
export interface TiktokAccountVM {
  uid: string;               // open_id
  username?: string | null;  // username
  name?: string | null;      // display_name
  picture?: string | null;   // avatar_url
}
export interface InstagramOauthResponse {
  access_token: string;
  user_id: string;
  name: string;
  picture: string;
  expires_in: number;
}

export interface NuvemShopOauthResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  store_id?: number;
  store_name?: string;
  domain?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class ProductService {


  path: string = '';
  path2: string = '';
  path3: string = '';


  constructor(
    private httpClient: HttpClient,
    private sanctum: SanctumService,
    private authHeaderService: AuthHeaderService,
    private revalidatedHttp: RevalidatedHttpService,
  ) {
  }

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.getAccessToken()}`
    })
  };


  public getProducts(): Observable<any> {
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/${this.path}`;

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.httpClient.get<any>(url, httpOptions).pipe(
          catchError((error) => {
            console.error('Erro ao buscar produtos:', error);
            return throwError(() => error);
          })
        )
      )
    );
  }





  public getInstagramAccounts(): Observable<{
    accounts: Array<{
      id: number;
      username: string;
      name: string;
      picture: string;
    }>;
  }> {
    // 1) Tenta ler SelectedStoreId direto do localStorage
    let selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    let store_id: number | undefined;

    // 2) Se não existir, tenta extrair de um JSON genérico "store"
    if (!selectedStoreId) {
      const storeJson = localStorage.getItem('store');
      if (storeJson) {
        try {
          const parsed = JSON.parse(storeJson);
          selectedStoreId = parsed?.SelectedStoreId ?? '';
        } catch {
          console.error('Erro ao parsear store do localStorage');
        }
      }
    }

    // 3) Converte para number se houver
    if (selectedStoreId) {
      store_id = Number(selectedStoreId);
    }

    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${store_id}/instagram_accounts`;

    // 4) Requisição com headers dinâmicos e CSRF
    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() =>
        from(this.authHeaderService.getHttpOptions()).pipe(
          switchMap(httpOptions =>
            this.httpClient
              .get<{
                accounts: Array<{ id: number; username: string; name: string; picture: string }>;
              }>(url, httpOptions)
              .pipe(
                catchError(error => {
                  console.error('Erro ao buscar contas do Instagram:', error);
                  return throwError(() => error);
                })
              )
          )
        )
      )
    );
  }


  public getFacebookAccounts(): Observable<{
    accounts: Array<{ id: string; name: string; picture: string }>;
  }> {
    const storeId = Number(localStorage.getItem('ss_SelectedStoreId'));

    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/facebook_accounts`;

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.httpClient.get<{ accounts: Array<{ id: string; name: string; picture: string }> }>(
          url,
          httpOptions
        )
      ),
      catchError(error => {
        console.error('Erro ao buscar contas do Facebook:', error);
        return throwError(error);
      })
    );
  }

  getIgAccountsForFacebookUser(facebookUserId: string): Observable<{ pages: any[] }> {
    const storeId = Number(localStorage.getItem('ss_SelectedStoreId'));
    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${storeId}/facebook-pages/ig-accounts?facebook_user_id=${facebookUserId}`;
    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(opts => this.httpClient.get<{ pages: any[] }>(url, opts))
    );
  }

  // Cache-first com ETag.
  getIgAccountsForFacebookUserRevalidated(facebookUserId: string): RevalidatedResource<{ pages: any[] }> {
    const storeId = Number(localStorage.getItem('ss_SelectedStoreId'));
    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${storeId}/facebook-pages/ig-accounts?facebook_user_id=${facebookUserId}`;
    const cacheKey = `ig_accounts_for_fb_user:store:${storeId}:fbuser:${facebookUserId}`;

    return this.revalidatedHttp.getSignal<{ pages: any[] }>(
      cacheKey,
      url,
      this.authHeaderService.getHttpOptions()
    );
  }

  updateMentionsSettings(pageId: number, mentionsEnabled: boolean, mentionsAutoPublish: boolean): Observable<any> {
    const storeId = Number(localStorage.getItem('ss_SelectedStoreId'));
    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${storeId}/facebook-pages/${pageId}/mentions-settings`;
    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(opts => this.httpClient.patch<any>(url, { mentions_enabled: mentionsEnabled, mentions_auto_publish: mentionsAutoPublish }, opts))
    );
  }

  getAppsPublic(): Observable<{ apps: Apps[] }> {
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/apps`;

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.httpClient.get<{ apps: Apps[] }>(url, httpOptions)
      ),
      catchError(error => throwError(() => error))
    );
  }
  getAppsPublicStore(): Observable<{ apps: Apps[] }> {
    const baseUrl = `${environment.apiBaseUrl}/api`;

    let selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    let store_id: number | undefined;

    if (!selectedStoreId) {
      const store = localStorage.getItem('store');
      if (store) {
        try {
          const parsedStore = JSON.parse(store);
          selectedStoreId = parsedStore?.SelectedStoreId || '';
        } catch (error) {
          console.error('Erro ao parsear store do localStorage:', error);
        }
      }
    }

    if (selectedStoreId) {
      store_id = Number(selectedStoreId);
    }

    const url = `${baseUrl}/admin/store/${store_id}/apps`;

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.httpClient.get<{ apps: Apps[] }>(url, httpOptions)
      ),
      catchError(error => throwError(() => error))
    );
  }

  // Cache-first com ETag.
  getAppsPublicStoreRevalidated(): RevalidatedResource<{ apps: Apps[] }> {
    const baseUrl = `${environment.apiBaseUrl}/api`;

    let selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    let store_id: number | undefined;

    if (!selectedStoreId) {
      const store = localStorage.getItem('store');
      if (store) {
        try {
          const parsedStore = JSON.parse(store);
          selectedStoreId = parsedStore?.SelectedStoreId || '';
        } catch (error) {
          console.error('Erro ao parsear store do localStorage:', error);
        }
      }
    }

    if (selectedStoreId) {
      store_id = Number(selectedStoreId);
    }

    const url = `${baseUrl}/admin/store/${store_id}/apps`;
    const cacheKey = `apps_public:store:${store_id}`;

    return this.revalidatedHttp.getSignal<{ apps: Apps[] }>(
      cacheKey,
      url,
      this.authHeaderService.getHttpOptions()
    );
  }

  private getNoCacheUrl(url: string): string {
    const timestamp = new Date().getTime(); // 🔥 Garante uma URL única
    return `${url}${url.includes('?') ? '&' : '?'}nocache=${timestamp}`;
  }



  //////////////////
  getDetailProduct(id: string): Observable<{ product: any }> {
    let selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    let store_id: number | undefined;

    if (!selectedStoreId) {
      const store = localStorage.getItem('store');
      if (store) {
        try {
          const parsedStore = JSON.parse(store);
          selectedStoreId = parsedStore?.SelectedStoreId || '';
        } catch (error) {
          console.error('Erro ao parsear store do localStorage:', error);
        }
      }
    }

    if (selectedStoreId) {
      store_id = Number(selectedStoreId);
    }

    const baseUrl = `${environment.apiBaseUrl}/api`;

    let url = `${baseUrl}/public/admin/store/${store_id}/app_product/${id}`;
    url = this.getNoCacheUrl(url);

    // ✅ rota pública: não usar authHeaderService (evita Authorization/withCredentials)
    const headers = new HttpHeaders({
      'X-Full-Referer': window.location.href,
    });

    return this.httpClient
      .get<{ product: any }>(url, {
        headers,
        withCredentials: false, // ✅ garante que não vai cookie
      })
      .pipe(
        catchError((error) => {
          console.error('Erro ao buscar detalhes do produto:', error);
          return throwError(() => error);
        })
      );
  }


  getChildremProducts(id: string): Observable<{ children: any[] }> {
    let selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    let store_id: number | undefined;

    if (!selectedStoreId) {
      const store = localStorage.getItem('store');
      if (store) {
        try {
          const parsedStore = JSON.parse(store);
          selectedStoreId = parsedStore?.SelectedStoreId || '';
        } catch (error) {
          console.error('Erro ao parsear store do localStorage:', error);
        }
      }
    }

    if (selectedStoreId) {
      store_id = Number(selectedStoreId);
    }

    const baseUrl = `${environment.apiBaseUrl}/api`;

    let url = `${baseUrl}/public/admin/store/${store_id}/app_product/${id}/children`;
    url = this.getNoCacheUrl(url);

    const headers = new HttpHeaders({
      'X-Full-Referer': window.location.href,
    });

    return this.httpClient
      .get<{ children: any[] }>(url, { headers, withCredentials: false })
      .pipe(
        catchError((error) => {
          console.error('Erro ao buscar produtos filhos:', error);
          return throwError(() => error);
        })
      );
  }

  getChildremProductsAttributes(id: string): Observable<{ attributes: any[] }> {
    let selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    let store_id: number | undefined;

    if (!selectedStoreId) {
      const store = localStorage.getItem('store');
      if (store) {
        try {
          const parsedStore = JSON.parse(store);
          selectedStoreId = parsedStore?.SelectedStoreId || '';
        } catch (error) {
          console.error('Erro ao parsear store do localStorage:', error);
        }
      }
    }

    if (selectedStoreId) {
      store_id = Number(selectedStoreId);
    }

    const baseUrl = `${environment.apiBaseUrl}/api`;

    let url = `${baseUrl}/public/admin/store/${store_id}/app_product/${id}/children/attributes`;
    url = this.getNoCacheUrl(url);

    const headers = new HttpHeaders({
      'X-Full-Referer': window.location.href,
    });

    return this.httpClient
      .get<{ attributes: any[] }>(url, { headers, withCredentials: false })
      .pipe(
        catchError((error) => {
          console.error('Erro ao buscar atributos dos produtos filhos:', error);
          return throwError(() => error);
        })
      );
  }

  //////////////////



  listProducts(): Observable<{ products: any[] }> {
    let selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    let store_id: number | undefined;

    if (!selectedStoreId) {
      const store = localStorage.getItem('store');
      if (store) {
        try {
          const parsedStore = JSON.parse(store);
          selectedStoreId = parsedStore?.SelectedStoreId || '';
        } catch (error) {
          console.error('Erro ao parsear store do localStorage:', error);
        }
      }
    }

    if (selectedStoreId) {
      store_id = Number(selectedStoreId);
    }

    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/public/admin/store/${store_id}/show_products`;

    return from(this.authHeaderService.getPublicHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.httpClient.get<{ products: any[] }>(url, httpOptions).pipe(
          catchError((error) => {
            console.error('Erro ao buscar detalhes do produto:', error);
            return throwError(() => error);
          })
        )
      )
    );
  }

  listCollections(): Observable<{ collections: any[] }> {
    let selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    let store_id: number | undefined;

    if (!selectedStoreId) {
      const store = localStorage.getItem('store');
      if (store) {
        try {
          const parsedStore = JSON.parse(store);
          selectedStoreId = parsedStore?.SelectedStoreId || '';
        } catch (error) {
          console.error('Erro ao parsear store do localStorage:', error);
        }
      }
    }

    if (selectedStoreId) {
      store_id = Number(selectedStoreId);
    }

    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/public/admin/store/${store_id}/list_collections`;

    // ✅ HEADER CORRETO PARA ROTA PÚBLICA
    return from(this.authHeaderService.getPublicHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.httpClient.get<{ collections: any[] }>(url, httpOptions)
      ),
      catchError(error => {
        console.error('Erro ao buscar coleções:', error);
        return throwError(() => error);
      })
    );
  }


  listArticles(): Observable<{ articles: any[] }> {
    let selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    let store_id: number | undefined;

    if (!selectedStoreId) {
      const store = localStorage.getItem('store');
      if (store) {
        try {
          const parsedStore = JSON.parse(store);
          selectedStoreId = parsedStore?.SelectedStoreId || '';
        } catch (error) {
          console.error('Erro ao parsear store do localStorage:', error);
        }
      }
    }

    if (selectedStoreId) {
      store_id = Number(selectedStoreId);
    }

    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/public/admin/store/${store_id}/list_articles`;

    // ✅ HEADER CORRETO PARA ROTA PÚBLICA
    return from(this.authHeaderService.getPublicHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.httpClient.get<{ articles: any[] }>(url, httpOptions)
      ),
      catchError(error => {
        console.error('Erro ao buscar artigos:', error);
        return throwError(() => error);
      })
    );
  }






  deleteGa4Account(ga4Id: string): Observable<any> {
    const selectedStoreId = Number(localStorage.getItem('ss_SelectedStoreId') || 0);
    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${selectedStoreId}/ga4/accounts/${encodeURIComponent(ga4Id)}`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeaderService.getHttpOptions())),
      switchMap(opts => this.httpClient.delete<any>(url, opts)),
      catchError(err => {
        console.error('Erro ao remover conta GA4:', err);
        return throwError(() => err);
      })
    );
  }


  deleteGoogleAccount(googleId: string): Observable<any> {
    const selectedStoreId = Number(localStorage.getItem('ss_SelectedStoreId') || 0);
    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${selectedStoreId}/google/accounts/${encodeURIComponent(googleId)}`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeaderService.getHttpOptions())),
      switchMap(opts => this.httpClient.delete<any>(url, opts)),
      catchError(err => {
        console.error('Erro ao remover conta Google:', err);
        return throwError(() => err);
      })
    );
  }

  deleteTiktokAccount(openId: string): Observable<RemoveTiktokResp> {
    const selectedStoreId = Number(localStorage.getItem('ss_SelectedStoreId') || 0);
    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${selectedStoreId}/tiktok/accounts/${encodeURIComponent(openId)}`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeaderService.getHttpOptions())),
      switchMap(opts => this.httpClient.delete<RemoveTiktokResp>(url, opts)),
      catchError(err => {
        console.error('Erro ao remover conta TikTok:', err);
        return throwError(() => err);
      })
    );
  }
  // product.service.ts
  getTiktokAccounts(): Observable<TiktokAccountVM[]> {
    const selectedStoreId = Number(localStorage.getItem('ss_SelectedStoreId') || 0);
    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${selectedStoreId}/tiktok/accounts`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeaderService.getHttpOptions())),
      switchMap(opts => this.httpClient.get<any>(url, opts)),
      map((res: any) => {
        const list = Array.isArray(res) ? res : (res.accounts ?? []);
        return list.map((a: any) => ({
          uid: String(a.id),                   // <-- id -> uid
          username: a.username ?? null,        // <-- username
          name: a.name ?? null,                // <-- name
          picture: a.picture ?? null           // <-- picture
        }) as TiktokAccountVM);
      }),
      catchError(err => {
        console.error('Erro ao buscar contas do TikTok:', err);
        return of([] as TiktokAccountVM[]);
      })
    );
  }

  // Cache-first com ETag — cacheia o body cru e reaplica a mesma normalização
  // de getTiktokAccounts() via computed(), pra não duplicar a lógica de mapeamento.
  getTiktokAccountsRevalidated(): RevalidatedResource<TiktokAccountVM[]> {
    const selectedStoreId = Number(localStorage.getItem('ss_SelectedStoreId') || 0);
    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${selectedStoreId}/tiktok/accounts`;
    const cacheKey = `tiktok_accounts:store:${selectedStoreId}`;

    const resource = this.revalidatedHttp.getSignal<any>(
      cacheKey,
      url,
      this.sanctum.initSanctum().then(() => this.authHeaderService.getHttpOptions())
    );

    const data = computed(() => {
      const res = resource.data();
      if (res === null) return null;
      const list = Array.isArray(res) ? res : (res.accounts ?? []);
      return list.map((a: any) => ({
        uid: String(a.id),
        username: a.username ?? null,
        name: a.name ?? null,
        picture: a.picture ?? null
      }) as TiktokAccountVM);
    });

    return { ...resource, data };
  }

  getNuvemshopAccounts(): Observable<NuvemshopAccountVM[]> {
    const selectedStoreId = Number(localStorage.getItem('ss_SelectedStoreId') || 0);
    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${selectedStoreId}/nuvemshop/accounts`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeaderService.getHttpOptions())),
      switchMap(opts =>
        this.httpClient.get<any>(url, opts).pipe(
          map((res: any) => {
            const list = Array.isArray(res) ? res : (res.accounts ?? []);
            return list.map((item: any) => ({
              uid: String(item.uid ?? item.id ?? item.store_id ?? item.domain ?? `nuvemshop-${Date.now()}-${Math.random()}`),
              store_name: item.store_name ?? item.name ?? null,
              domain: item.domain ?? item.shop_domain ?? null,
              picture: item.picture ?? null,
            }) as NuvemshopAccountVM);
          }),
          catchError(err => {
            console.error('Erro ao buscar contas da Nuvemshop:', err);
            return of([] as NuvemshopAccountVM[]);
          })
        )
      )
    );
  }

  // Cache-first com ETag — mesma normalização de getNuvemshopAccounts() via computed().
  getNuvemshopAccountsRevalidated(): RevalidatedResource<NuvemshopAccountVM[]> {
    const selectedStoreId = Number(localStorage.getItem('ss_SelectedStoreId') || 0);
    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${selectedStoreId}/nuvemshop/accounts`;
    const cacheKey = `nuvemshop_accounts:store:${selectedStoreId}`;

    const resource = this.revalidatedHttp.getSignal<any>(
      cacheKey,
      url,
      this.sanctum.initSanctum().then(() => this.authHeaderService.getHttpOptions())
    );

    const data = computed(() => {
      const res = resource.data();
      if (res === null) return null;
      const list = Array.isArray(res) ? res : (res.accounts ?? []);
      return list.map((item: any) => ({
        uid: String(item.uid ?? item.id ?? item.store_id ?? item.domain ?? `nuvemshop-${Date.now()}-${Math.random()}`),
        store_name: item.store_name ?? item.name ?? null,
        domain: item.domain ?? item.shop_domain ?? null,
        picture: item.picture ?? null,
      }) as NuvemshopAccountVM);
    });

    return { ...resource, data };
  }




  getGa4Accounts(): Observable<Ga4AccountVM[]> {
    const selectedStoreId = Number(localStorage.getItem('ss_SelectedStoreId') || 0);
    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${selectedStoreId}/ga4/accounts`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeaderService.getHttpOptions())),
      switchMap(opts => this.httpClient.get<any>(url, opts)),
      map((res: any) => {
        const list = Array.isArray(res) ? res : (res.accounts ?? []);
        return list.map((a: any) => ({
          uid: String(a.id),
          accountId: a.accountId ?? null,
          accountName: a.accountName ?? null,
          email: a.email ?? null,
          picture: a.picture ?? null,
          locale: a.locale ?? null,
          status: a.status ?? null,
          created_at: a.created_at ?? null,
          properties: a.properties ?? [],
          selectedPropertyDetails: Array.isArray(a.property_array)
            ? a.property_array.map((p: any) => typeof p === 'string'
                ? { propertyId: p, propertyName: p, measurementId: null }
                : { propertyId: p.propertyId, propertyName: p.propertyName ?? p.propertyId, measurementId: p.measurementId ?? null })
            : [],
          selectedProperties: Array.isArray(a.property_array)
            ? a.property_array.map((p: any) => typeof p === 'string' ? p : p.propertyId)
            : [],
        }) as Ga4AccountVM);

      })
    );
  }

  // Cache-first com ETag — mesma normalização de getGa4Accounts() via computed().
  getGa4AccountsRevalidated(): RevalidatedResource<Ga4AccountVM[]> {
    const selectedStoreId = Number(localStorage.getItem('ss_SelectedStoreId') || 0);
    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${selectedStoreId}/ga4/accounts`;
    const cacheKey = `ga4_accounts:store:${selectedStoreId}`;

    const resource = this.revalidatedHttp.getSignal<any>(
      cacheKey,
      url,
      this.sanctum.initSanctum().then(() => this.authHeaderService.getHttpOptions())
    );

    const data = computed(() => {
      const res = resource.data();
      if (res === null) return null;
      const list = Array.isArray(res) ? res : (res.accounts ?? []);
      return list.map((a: any) => ({
        uid: String(a.id),
        accountId: a.accountId ?? null,
        accountName: a.accountName ?? null,
        email: a.email ?? null,
        picture: a.picture ?? null,
        locale: a.locale ?? null,
        status: a.status ?? null,
        created_at: a.created_at ?? null,
        properties: a.properties ?? [],
        selectedPropertyDetails: Array.isArray(a.property_array)
          ? a.property_array.map((p: any) => typeof p === 'string'
              ? { propertyId: p, propertyName: p, measurementId: null }
              : { propertyId: p.propertyId, propertyName: p.propertyName ?? p.propertyId, measurementId: p.measurementId ?? null })
          : [],
        selectedProperties: Array.isArray(a.property_array)
          ? a.property_array.map((p: any) => typeof p === 'string' ? p : p.propertyId)
          : [],
      }) as Ga4AccountVM);
    });

    return { ...resource, data };
  }

  saveGa4CustomDimensions(ga4AccountId: string, allProperties: any[], selectedPropertyIds: string[]): Observable<any> {
    const selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${selectedStoreId}/ga4/accounts/${ga4AccountId}/custom-dimensions`;
    const payload = { allProperties, selectedPropertyIds };
    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeaderService.getHttpOptions())),
      switchMap(opts => this.httpClient.put<any>(url, payload, opts)),
      catchError(err => throwError(() => err))
    );
  }


  getGoogleAccounts(): Observable<GoogleAccountVM[]> {
    const selectedStoreId = Number(localStorage.getItem('ss_SelectedStoreId') || 0);
    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${selectedStoreId}/google/accounts`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeaderService.getHttpOptions())),
      switchMap(opts => this.httpClient.get<any>(url, opts)),
      map((res: any) => {
        const list = Array.isArray(res) ? res : (res.accounts ?? []);
        return list.map((a: any) => ({
          uid: String(a.id),
          username: a.username ?? null,
          name: a.name ?? null,
          picture: a.picture ?? null,
          email: a.email ?? null,
          locale: a.locale ?? null,
          status: a.status ?? null
        }) as GoogleAccountVM);
      }),
      catchError(err => {
        console.error('Erro ao buscar contas do Google:', err);
        return of([] as GoogleAccountVM[]);
      })
    );
  }

  // Cache-first com ETag — mesma normalização de getGoogleAccounts() via computed().
  getGoogleAccountsRevalidated(): RevalidatedResource<GoogleAccountVM[]> {
    const selectedStoreId = Number(localStorage.getItem('ss_SelectedStoreId') || 0);
    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${selectedStoreId}/google/accounts`;
    const cacheKey = `google_accounts:store:${selectedStoreId}`;

    const resource = this.revalidatedHttp.getSignal<any>(
      cacheKey,
      url,
      this.sanctum.initSanctum().then(() => this.authHeaderService.getHttpOptions())
    );

    const data = computed(() => {
      const res = resource.data();
      if (res === null) return null;
      const list = Array.isArray(res) ? res : (res.accounts ?? []);
      return list.map((a: any) => ({
        uid: String(a.id),
        username: a.username ?? null,
        name: a.name ?? null,
        picture: a.picture ?? null,
        email: a.email ?? null,
        locale: a.locale ?? null,
        status: a.status ?? null
      }) as GoogleAccountVM);
    });

    return { ...resource, data };
  }




  getAppsStore(key?: string): Observable<StoreAppsResponse> {
    const selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    const store_id = selectedStoreId ? Number(selectedStoreId) : null;

    const baseUrl = `${environment.apiBaseUrl}/api`;

    let url = `${baseUrl}/admin/store/${store_id}/store_apps`;
    if (key) {
      url += `?key=${encodeURIComponent(key)}`;
    }

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.httpClient.get<StoreAppsResponse>(url, httpOptions).pipe(
          map(response => ({
            ...response,
            store_apps: (response.store_apps || []).map((app: any) => ({
              ...app,
              uid: app.uid ?? app.UID
            }))
          })),
          catchError(error => {
            console.error('Erro ao buscar apps:', error);
            return throwError(() => error);
          })
        )
      )
    );
  }

  // Cache-first com ETag — mesma normalização de getAppsStore() via computed().
  getAppsStoreRevalidated(key?: string): RevalidatedResource<StoreAppsResponse> {
    const selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    const store_id = selectedStoreId ? Number(selectedStoreId) : null;

    const baseUrl = `${environment.apiBaseUrl}/api`;

    let url = `${baseUrl}/admin/store/${store_id}/store_apps`;
    if (key) {
      url += `?key=${encodeURIComponent(key)}`;
    }

    const cacheKey = `store_apps:store:${store_id}:key:${key || ''}`;

    const resource = this.revalidatedHttp.getSignal<StoreAppsResponse>(
      cacheKey,
      url,
      this.authHeaderService.getHttpOptions()
    );

    const data = computed(() => {
      const res = resource.data();
      if (res === null) return null;
      return {
        ...res,
        store_apps: (res.store_apps || []).map((app: any) => ({
          ...app,
          uid: app.uid ?? app.UID
        }))
      };
    });

    return { ...resource, data };
  }

  getStoreIntegration(storeKey?: string): Observable<StoreIntegrationResponse> {
    const selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    const store_id = selectedStoreId ? Number(selectedStoreId) : null;

    const baseUrl = `${environment.apiBaseUrl}/api`;

    let url = `${baseUrl}/admin/store/${store_id}/store_app`;
    if (storeKey) {
      url += `?store=${encodeURIComponent(storeKey)}`;
    }

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.httpClient.get<StoreIntegrationResponse>(url, httpOptions).pipe(
          catchError(error => {
            console.error('Erro ao buscar integração da loja:', error);
            return throwError(() => error);
          })
        )
      )
    );
  }

  generateStoreEditToken(): Observable<StoreEditTokenResponse> {
    const selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    const store_id = selectedStoreId ? Number(selectedStoreId) : null;

    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${store_id}/genarate_token`;

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.httpClient.get<StoreEditTokenResponse>(url, httpOptions).pipe(
          catchError(error => {
            console.error('Erro ao gerar token temporário da loja:', error);
            return throwError(() => error);
          })
        )
      )
    );
  }


  saveGa4Properties(ga4AccountId: string, properties: string[]): Observable<any> {
    const selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${selectedStoreId}/ga4/accounts/${ga4AccountId}/properties`;

    const payload = {
      properties
    };

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeaderService.getHttpOptions())),
      switchMap(opts => this.httpClient.put<any>(url, payload, opts)),
      catchError(err => {
        console.error('Erro ao salvar propriedades GA4:', err);
        return throwError(() => err);
      })
    );
  }


  createAppsStore(key: string): Observable<StoreAppCreationResponse> {
    const selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    const store_id = selectedStoreId ? Number(selectedStoreId) : null;

    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${store_id}/store_apps`;

    const payload = {
      key,
      status: 'active', // Status padrão
    };

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() =>
        from(this.authHeaderService.getHttpOptions()).pipe(
          switchMap(httpOptions =>
            this.httpClient.post<StoreAppCreationResponse>(url, payload, httpOptions).pipe(
              catchError((error) => {
                console.error('Erro ao criar integração:', error);
                return throwError(() => error);
              })
            )
          )
        )
      )
    );
  }


  createAppsStore2(key: string, customPayload?: any): Observable<StoreAppCreationResponse> {
    const selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    const store_id = selectedStoreId ? Number(selectedStoreId) : null;

    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${store_id}/store_apps`;

    const defaultPayload = {
      key,
      status: 'active',
    };

    const payload = { ...defaultPayload, ...customPayload };

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() =>
        from(this.authHeaderService.getHttpOptions()).pipe(
          switchMap(httpOptions =>
            this.httpClient.post<StoreAppCreationResponse>(url, payload, httpOptions).pipe(
              catchError((error) => {
                console.error('Erro ao criar integração:', error);
                return throwError(() => error);
              })
            )
          )
        )
      )
    );
  }



  instagramOauth(code: string, state: string): Observable<InstagramOauthResponse> {
    let store_id = 163;
    const selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    if (selectedStoreId) {
      store_id = Number(selectedStoreId);
    }

    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${store_id}/instagram/oauth/callback`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() =>
        from(this.authHeaderService.getHttpOptions()).pipe(
          switchMap(httpOptions =>
            this.httpClient.post<InstagramOauthResponse>(url, { code, state }, httpOptions).pipe(
              catchError((error) => {
                console.error('Erro na autenticação Instagram:', error);
                return throwError(() => error);
              })
            )
          )
        )
      )
    );
  }

  tiktokOauth(code: string, state: string): Observable<TikTokOauthResponse> {
    let store_id;
    const selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    if (selectedStoreId) store_id = Number(selectedStoreId);

    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${store_id}/tiktok/oauth/callback`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeaderService.getHttpOptions())),
      switchMap(httpOptions =>
        this.httpClient.post<TikTokOauthResponse>(url, { code, state }, httpOptions).pipe(
          catchError((error) => {
            console.error('Erro na autenticação TikTok:', error);
            return throwError(() => error);
          })
        )
      )
    );
  }

  googleOauth(code: string, state: string): Observable<any> {
    let store_id;
    const selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    if (selectedStoreId) store_id = Number(selectedStoreId);

    const baseUrl = `${environment.apiBaseUrl}/api`;

    // Altere o endpoint conforme sua rota backend
    const url = `${baseUrl}/admin/store/${store_id}/google/oauth/callback`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeaderService.getHttpOptions())),
      switchMap(httpOptions =>
        this.httpClient.post<any>(url, { code, state }, httpOptions).pipe(
          catchError((error) => {
            console.error('Erro na autenticação Google:', error);
            return throwError(() => error);
          })
        )
      )
    );
  }

  nuvemshopOauth(code: string, storeIdParam: string, state?: string): Observable<NuvemShopOauthResponse> {
    const selectedStoreId = Number(localStorage.getItem('ss_SelectedStoreId') || 0);
    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${selectedStoreId}/nuvemshop/oauth/callback`;

    const numericStoreId = Number(storeIdParam);
    const payload: any = {
      code,
      store_id: Number.isNaN(numericStoreId) ? storeIdParam : numericStoreId,
    };

    if (state) {
      payload.state = state;
    }

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeaderService.getHttpOptions())),
      switchMap(httpOptions =>
        this.httpClient.post<NuvemShopOauthResponse>(url, payload, httpOptions).pipe(
          catchError((error) => {
            console.error('Erro na autenticaÇõÇœo Nuvemshop:', error);
            return throwError(() => error);
          })
        )
      )
    );
  }

  ga4Oauth(code: string, state: string): Observable<any> {
    let store_id;
    const selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    if (selectedStoreId) store_id = Number(selectedStoreId);

    const baseUrl = `${environment.apiBaseUrl}/api`;

    // Altere para a rota do seu backend GA4!
    const url = `${baseUrl}/admin/store/${store_id}/ga4/oauth/callback`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeaderService.getHttpOptions())),
      switchMap(httpOptions =>
        this.httpClient.post<any>(url, { code, state }, httpOptions).pipe(
          catchError((error) => {
            console.error('Erro na autenticação GA4:', error);
            return throwError(() => error);
          })
        )
      )
    );
  }



  authCallback(store_id: number, shop: string, code: string): Observable<any> {
    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${store_id}/auth/callback`;

    const body = { shop, code };

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() =>
        from(this.authHeaderService.getHttpOptions()).pipe(
          switchMap(httpOptions =>
            this.httpClient.post<any>(url, body, httpOptions).pipe(
              catchError((error) => {
                console.error('Erro na autenticação Shopify:', error);
                return throwError(() => error);
              })
            )
          )
        )
      )
    );
  }


  removeAppsStore(uid: string): Observable<StoreAppsResponse> {
    const selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    const store_id = selectedStoreId ? Number(selectedStoreId) : null;

    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${store_id}/store_apps/${uid}`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() =>
        from(this.authHeaderService.getHttpOptions()).pipe(
          switchMap(httpOptions =>
            this.httpClient.delete<StoreAppsResponse>(url, httpOptions).pipe(
              catchError((error) => {
                console.error('Erro ao buscar apps:', error);
                return throwError(() => error);
              })
            )
          )
        )
      )
    );
  }


  destroyInstagramAccount(uid: string): Observable<StoreAppsResponse> {
    const selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
    const store_id = selectedStoreId ? Number(selectedStoreId) : null;

    const baseUrl = `${environment.apiBaseUrl}/api`;
    const url = `${baseUrl}/admin/store/${store_id}/instagram_accounts/${uid}`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() =>
        from(this.authHeaderService.getHttpOptions()).pipe(
          switchMap(httpOptions =>
            this.httpClient.delete<StoreAppsResponse>(url, httpOptions).pipe(
              catchError((error) => {
                console.error('Erro ao buscar apps:', error);
                return throwError(() => error);
              })
            )
          )
        )
      )
    );
  }



  public getCollections(): Observable<any> {
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/${this.path3}`;

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.httpClient.get<any>(url, httpOptions).pipe(
          catchError((error) => {
            console.error('Erro ao buscar coleções:', error);
            return throwError(() => error);
          })
        )
      )
    );
  }


  public getCategorys(): Observable<any> {
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/${this.path2}`;

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.httpClient.get<any>(url, httpOptions).pipe(
          catchError((error) => {
            console.error('Erro ao buscar categorias:', error);
            return throwError(() => error);
          })
        )
      )
    );
  }

  // listWidgetInfluencers(store_id: number, widget_id: number): Observable<{ influencers: Array<{ label: string; value: string; avatar?: string | null }> }> {
  //   const baseUrl = environment.production
  //     ? 'https://api.slicksell.com.br/api'
  //     : 'https://stage-api.slicksell.com.br/api';

  //   let url = `${baseUrl}/public/admin/store/${store_id}/widget/${widget_id}/listInfluencers`;
  //   url = this.getNoCacheUrl(url);

  //   const headers = new HttpHeaders({
  //     'X-Full-Referer': window.location.href,
  //   });

  //   return this.httpClient.get<{ influencers: Array<{ label: string; value: string; avatar?: string | null }> }>(url, {
  //     headers,
  //     withCredentials: false,
  //   }).pipe(
  //     catchError((error) => {
  //       console.error('Erro ao listar influenciadores do widget:', error);
  //       return throwError(() => error);
  //     })
  //   );
  // }
  //import { HttpHeaders, HttpParams } from '@angular/common/http';

  listWidgetInfluencers(
    store_id: number,
    widget_id: number,
    params?: { page?: number; limit?: number; q?: string }
  ): Observable<{
    influencers: Array<{ label: string; value: string; avatar?: string | null }>;
    total: number;
    page: number;
    limit: number;
    lastPage: number;
    hasMore: boolean;
  }> {

    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/public/admin/store/${store_id}/widget/${widget_id}/listInfluencers`;

    const headers = new HttpHeaders({
      'X-Full-Referer': window.location.href,
    });

    let httpParams = new HttpParams();
    if (params?.page != null) httpParams = httpParams.set('page', String(params.page));
    if (params?.limit != null) httpParams = httpParams.set('limit', String(params.limit));
    if (params?.q) httpParams = httpParams.set('q', params.q);

    return this.httpClient.get<{
      influencers: any[];
      total: number;
      page: number;
      limit: number;
      lastPage: number;
      hasMore: boolean;
    }>(url, {
      headers,
      params: httpParams,
      withCredentials: false,
    });
  }

  listWidgetCategories(
    store_id: number,
    widget_id: number,
    params?: { page?: number; limit?: number; q?: string }
  ): Observable<{
    categories: Array<{ label: string; value: number }>;
    total: number;
    page: number;
    limit: number;
    lastPage: number;
    hasMore: boolean;
  }> {

    const baseUrl = `${environment.apiBaseUrl}/api`;

    let url = `${baseUrl}/public/admin/store/${store_id}/widget/${widget_id}/listCategories`;
    url = this.getNoCacheUrl(url);

    const headers = new HttpHeaders({
      'X-Full-Referer': window.location.href,
    });

    let httpParams = new HttpParams();
    if (params?.page != null) httpParams = httpParams.set('page', String(params.page));
    if (params?.limit != null) httpParams = httpParams.set('limit', String(params.limit));
    if (params?.q) httpParams = httpParams.set('q', params.q);

    return this.httpClient.get<{
      categories: any[];
      total: number;
      page: number;
      limit: number;
      lastPage: number;
      hasMore: boolean;
    }>(url, {
      headers,
      params: httpParams,
      withCredentials: false,
    }).pipe(
      catchError((error) => {
        console.error('Erro ao listar categorias do widget:', error);
        return throwError(() => error);
      })
    );
  }

  getAccessToken(): string {
    return localStorage.getItem("access_token") || '';
  }

}
